import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import {
  Check, X, Star, Phone, Calculator, FileText, Download,
  Shield, Wrench, Search, Zap, AlertTriangle, ArrowRight,
  Clock, DollarSign, Home, HeartHandshake, Ban, CircleCheck
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

/* ──────────────────────────────────────────────
   PRICING DATA (annual-only)
   ────────────────────────────────────────────── */
const pricingMemberships = [
  {
    name: 'TIER 1',
    subtitle: 'Appliance Protection',
    yearlyPrice: 399,
    color: 'from-yellow-500 to-amber-600',
    accentColor: 'text-yellow-400',
    borderAccent: 'border-yellow-500/50',
    bgAccent: 'bg-yellow-500/10',
    tagline: 'Best for keeping essential appliances running efficiently',
    benefits: [
      'Regular inspections to catch performance issues early',
      'Basic cleaning to extend appliance life',
      'Reduce the risk of unexpected appliance failures',
      'No service call fees',
      'Access to repair support after 12 months'
    ],
    highlight: false,
    servicesCovered: 1,
    details: {
      covered: [
        'Appliance inspections & performance checks',
        'Basic cleaning (coils, filters, vents where applicable)',
        'Photo documentation & wear-and-tear reporting',
        'No diagnostic or service call fees',
        'Appliance parts billed at cost',
        'Major Repair Benefit (after 12 months):',
        '• 1 major appliance repair per year',
        '• Labor covered up to 2 hours',
        '• Parts paid at cost'
      ],
      notCovered: [
        'Sealed systems',
        'Washer gear cases',
        'Full appliance replacements',
        'Cosmetic damage',
        'Plumbing systems',
        'Electrical systems'
      ],
      terms: [
        '2 visits per year (every 6 months)',
        'Annual membership — billed once per year',
        'Major repair benefit available after 12 months of membership',
        'Labor covered up to 2 hours for major repairs',
        'Parts always billed at cost (no markup)',
        'This is not a home warranty - it\'s smarter maintenance'
      ]
    }
  },
  {
    name: 'TIER 2',
    subtitle: 'Essential Systems Protection',
    yearlyPrice: 599,
    color: 'from-teal-500 to-emerald-600',
    accentColor: 'text-teal-400',
    borderAccent: 'border-teal-500/50',
    bgAccent: 'bg-teal-500/10',
    tagline: 'Covers plumbing + electrical systems homeowners rely on daily',
    benefits: [
      'Keep plumbing and electrical systems running efficiently year-round',
      'Reduce the chance of breakdowns and water damage',
      'Catch plumbing issues before they turn into costly damage',
      'No diagnostic or service call fees',
      'Annual labor credit + repair support'
    ],
    highlight: true,
    badge: 'MOST POPULAR',
    servicesCovered: 2,
    details: {
      covered: [
        'Plumbing health inspections & freeze-risk checks',
        'Electrical system visual inspections',
        'No diagnostic or service call fees',
        'Plumbing & electrical parts billed at cost',
        'Annual labor credit ($100-$150) - after 12 months',
        'Major Repair Benefit (after 12 months):',
        '• 1 major repair labor event per year',
        '• 2-hour labor cap per event'
      ],
      notCovered: [
        'Appliances (covered in Tier 1)',
        'Full system replacement',
        'Refrigerant recharge',
        'Sewer lines',
        'Structural plumbing',
        'Electrical systems'
      ],
      terms: [
        '3 visits per year (every 4 months)',
        'Annual membership — billed once per year',
        'Major repair benefit available after 12 months of membership',
        'Labor credit of $100-$150 applied annually after first year',
        'Parts always billed at cost (no markup)',
        'This is not a home warranty - it\'s smarter maintenance'
      ]
    }
  },
  {
    name: 'TIER 3',
    subtitle: 'Total Home Protection',
    yearlyPrice: 799,
    color: 'from-blue-500 to-indigo-600',
    accentColor: 'text-blue-400',
    borderAccent: 'border-blue-500/50',
    bgAccent: 'bg-blue-500/10',
    tagline: 'Full-home support for homeowners who want peace of mind',
    benefits: [
      'Ongoing maintenance across major home systems',
      'Reduce the likelihood of large, unexpected repair costs',
      'Priority service and faster response times',
      'Multiple major repair support events per year',
      'Lower labor costs across all services'
    ],
    highlight: false,
    servicesCovered: 5,
    details: {
      covered: [
        'Appliance, plumbing, electrical (visual), handyman oversight',
        'One annual full-home maintenance & inspection visit',
        'Quarterly whole-home check-ins',
        'Parts billed at cost across all covered systems',
        'TWO major repair labor events per year (2-hour cap each)',
        'Higher annual labor credit ($250-$300) - after 12 months',
        'VIP escalation & support',
        '10% off labor on non-covered work',
        'Lower member labor rates across all trades'
      ],
      notCovered: [
        'Sealed systems',
        'Full system replacements',
        'Structural repairs',
        'Cosmetic upgrades',
        'Roofing',
        'Foundation work'
      ],
      terms: [
        '4 visits per year (every 3 months)',
        'Annual membership — billed once per year',
        'Two major repair events per year with 2-hour labor cap each',
        'Annual labor credit of $250-$300',
        'Parts always billed at cost (no markup)',
        'Quick inspection visits: $35 flat per visit',
        'Full home inspection: $50/hour (2 hours)',
        'This is not a home warranty - it\'s smarter maintenance'
      ]
    }
  }
];

/* ──────────────────────────────────────────────
   ANIMATION VARIANTS
   ────────────────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

/* ──────────────────────────────────────────────
   SECTION DIVIDER
   ────────────────────────────────────────────── */
const SectionDivider = () => (
  <div className="flex items-center justify-center py-4">
    <div className="h-px w-16 bg-slate-700" />
    <div className="h-1.5 w-1.5 rounded-full bg-orange-500 mx-3" />
    <div className="h-px w-16 bg-slate-700" />
  </div>
);

/* ══════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════ */
export const MembershipSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    membership: ''
  });
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error('Please fill in your name and phone number');
      return;
    }
    setIsSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/callback-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email || null,
          plan: formData.membership
        }),
      });
      const data = await response.json();
      if (data.success) {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', { 'send_to': 'AW-17881958235/PI-fCLWyn_AbENuO5M5C' });
        }
        toast.success("Request received! We'll call you within 24 hours to discuss your membership options.");
        setFormData({ name: '', phone: '', email: '', membership: '' });
        setShowForm(false);
      } else {
        toast.error(data.message || 'Failed to submit. Please try calling us directly.');
      }
    } catch (error) {
      console.error('Callback request error:', error);
      toast.error('Failed to submit. Please try calling us at (234) 205-1010.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMembershipSelect = (membershipName) => {
    setFormData({ ...formData, membership: membershipName });
    setShowForm(true);
    setTimeout(() => {
      document.querySelector('#callback-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  return (
    <div id="maintenance-membership">
      {/* ═══════════════════════════════════════
          SECTION 1 — HERO
          ═══════════════════════════════════════ */}
      <section className="py-16 sm:py-20 lg:py-28 presidential-gradient" data-testid="membership-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" variants={stagger} viewport={{ once: true }}>
            <motion.h2
              variants={fadeUp}
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6"
            >
              Stop Paying for Repairs.{' '}
              <span className="bg-gradient-to-r from-orange-400 to-amber-500 bg-clip-text text-transparent">
                Start Preventing Them.
              </span>
            </motion.h2>

            <motion.p variants={fadeUp} className="text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto mb-10 leading-relaxed">
              Most homeowners wait until something breaks — then deal with the cost, stress, and delays.
              We help you avoid those problems entirely with ongoing home maintenance and support.
            </motion.p>

            {/* Callout Bar */}
            <motion.div variants={fadeUp}>
              <Card className="max-w-2xl mx-auto border border-orange-500/30 bg-orange-500/5 backdrop-blur">
                <CardContent className="p-5 sm:p-6">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <AlertTriangle className="h-5 w-5 text-orange-400" />
                    <span className="text-sm font-bold text-orange-400 uppercase tracking-wider">Not a Home Warranty</span>
                  </div>
                  <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-sm font-medium text-slate-300">
                    <span>No claims</span>
                    <span className="text-slate-600 hidden sm:inline">|</span>
                    <span>No approvals</span>
                    <span className="text-slate-600 hidden sm:inline">|</span>
                    <span>No waiting weeks</span>
                  </div>
                  <p className="text-slate-400 text-sm mt-3">
                    Just proactive care and fast support when you need it.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 2 — HOW IT WORKS
          ═══════════════════════════════════════ */}
      <section className="py-16 sm:py-20 bg-slate-900/50" data-testid="membership-how-it-works">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" variants={stagger} viewport={{ once: true }}>
            <motion.div variants={fadeUp} className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                How RemodWorks Works
              </h2>
              <div className="h-1 w-16 bg-orange-500 mx-auto rounded-full" />
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: Home,
                  step: '01',
                  title: 'We Maintain Your Home',
                  desc: 'We visit your home multiple times per year to inspect, clean, and service key systems.'
                },
                {
                  icon: Search,
                  step: '02',
                  title: 'We Catch Problems Early',
                  desc: 'Small issues are handled before they turn into expensive breakdowns.'
                },
                {
                  icon: Shield,
                  step: '03',
                  title: 'You Avoid Big Repairs',
                  desc: 'Regular maintenance keeps your systems running longer and more efficiently.'
                },
                {
                  icon: Wrench,
                  step: '04',
                  title: 'If Something Breaks',
                  desc: 'No claims. No approvals. We help you fix it fast with priority service and reduced member costs.'
                }
              ].map((item, idx) => (
                <motion.div key={idx} variants={fadeUp}>
                  <Card className="h-full border border-slate-700 bg-slate-800/40 hover:border-slate-600 transition-colors duration-200" data-testid={`how-it-works-step-${idx + 1}`}>
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-orange-500/10 border border-orange-500/30 mb-4">
                        <item.icon className="h-6 w-6 text-orange-400" />
                      </div>
                      <div className="text-xs font-bold text-orange-500 tracking-widest mb-2">STEP {item.step}</div>
                      <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                      <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 3 — WHY THIS IS DIFFERENT
          ═══════════════════════════════════════ */}
      <section className="py-16 sm:py-20 presidential-gradient" data-testid="membership-comparison">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" variants={stagger} viewport={{ once: true }}>
            <motion.div variants={fadeUp} className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Home Warranties React.{' '}
                <span className="text-orange-400">We Prevent.</span>
              </h2>
              <div className="h-1 w-16 bg-orange-500 mx-auto rounded-full" />
            </motion.div>

            <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Home Warranties Column */}
              <Card className="border border-red-500/30 bg-red-500/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-xl font-bold text-red-400 text-center">Home Warranties</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="space-y-3">
                    {[
                      'Wait for something to break',
                      'Require claims + approvals',
                      'Charge service fees',
                      'Deny or delay coverage',
                      'You deal with the stress'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <X className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* RemodWorks Column */}
              <Card className="border border-green-500/30 bg-green-500/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-xl font-bold text-green-400 text-center">RemodWorks</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="space-y-3">
                    {[
                      'Maintain your home year-round',
                      'Catch issues before they escalate',
                      'No claims or approvals',
                      'Faster service when needed',
                      'We help you stay ahead of problems'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 4 — COST REALITY
          ═══════════════════════════════════════ */}
      <section className="py-16 sm:py-20 bg-slate-900/50" data-testid="membership-cost-reality">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" variants={stagger} viewport={{ once: true }}>
            <motion.div variants={fadeUp}>
              <DollarSign className="h-10 w-10 text-orange-400 mx-auto mb-4" />
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                The Real Cost of Waiting Until Something Breaks
              </h2>
            </motion.div>

            <motion.div variants={fadeUp} className="space-y-4 text-lg text-slate-400 leading-relaxed">
              <p>
                Most homeowners spend{' '}
                <span className="text-white font-semibold">$1,000 – $3,000 per year</span>{' '}
                on unexpected repairs.
              </p>
              <p>
                The problem isn't just the cost — it's the timing.
                Breakdowns happen when you least expect them.
              </p>
              <p className="text-orange-300 font-medium">
                RemodWorks helps you stay ahead of those problems so you're not stuck reacting.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 5 — REAL SAVINGS EXAMPLE
          ═══════════════════════════════════════ */}
      <section className="py-16 sm:py-20 presidential-gradient" data-testid="membership-savings-example">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" variants={stagger} viewport={{ once: true }}>
            <motion.div variants={fadeUp} className="text-center mb-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Real Example: Dryer Repair
              </h2>
              <div className="h-1 w-16 bg-orange-500 mx-auto rounded-full" />
            </motion.div>

            <motion.div variants={fadeUp}>
              <Card className="border border-orange-500/30 bg-slate-800/50 backdrop-blur shadow-xl" data-testid="savings-comparison-card">
                <CardContent className="p-6 sm:p-8">
                  <div className="grid grid-cols-2 gap-4 sm:gap-6">
                    {/* Without Membership */}
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5 text-center">
                      <div className="text-xs font-bold text-red-400 uppercase tracking-wider mb-4">Without Membership</div>
                      <div className="space-y-2 text-sm text-slate-300 mb-4">
                        <div className="flex justify-between px-2">
                          <span>Labor</span><span className="font-semibold text-white">$180</span>
                        </div>
                        <div className="flex justify-between px-2">
                          <span>Parts</span><span className="font-semibold text-white">$60</span>
                        </div>
                      </div>
                      <div className="pt-4 border-t border-red-500/30">
                        <div className="text-3xl font-bold text-red-400">$240</div>
                      </div>
                    </div>

                    {/* With Membership */}
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5 text-center">
                      <div className="text-xs font-bold text-green-400 uppercase tracking-wider mb-4">With RemodWorks</div>
                      <div className="space-y-2 text-sm text-slate-300 mb-4">
                        <div className="flex justify-between px-2">
                          <span>Labor</span><span className="font-semibold text-green-400">$0</span>
                        </div>
                        <div className="flex justify-between px-2">
                          <span>Parts</span><span className="font-semibold text-green-400">$11</span>
                        </div>
                      </div>
                      <div className="pt-4 border-t border-green-500/30">
                        <div className="text-3xl font-bold text-green-400">$11</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 text-center">
                    <Badge className="bg-green-600/90 text-white px-6 py-2 text-base font-bold shadow-lg" data-testid="savings-badge">
                      That's $229 saved on just one repair.
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 6 — MEMBERSHIP OPTIONS (PRICING)
          ═══════════════════════════════════════ */}
      <section id="pricing" className="py-16 sm:py-20 bg-slate-900/50" data-testid="membership-pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" variants={stagger} viewport={{ once: true }}>
            <motion.div variants={fadeUp} className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Choose Your Level of Home Protection
              </h2>
              <div className="h-1 w-16 bg-orange-500 mx-auto rounded-full" />
            </motion.div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-8">
              {pricingMemberships.map((membership, index) => (
                <motion.div
                  key={membership.name}
                  variants={fadeUp}
                  className="relative"
                >
                  {membership.badge && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                      <Badge className="bg-orange-500 text-white px-4 py-1.5 text-xs font-bold shadow-lg">
                        <Star className="h-3 w-3 mr-1 inline" />
                        {membership.badge}
                      </Badge>
                    </div>
                  )}
                  <Card
                    className={`h-full border bg-slate-800/50 overflow-hidden ${
                      membership.highlight
                        ? 'shadow-2xl md:scale-105 border-orange-500/40 ring-1 ring-orange-500/20'
                        : 'shadow-md border-slate-700 hover:border-slate-600'
                    } transition-all duration-200`}
                    data-testid={`pricing-card-${membership.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {/* Gradient Header */}
                    <CardHeader className={`bg-gradient-to-br ${membership.color} text-white p-6`}>
                      <CardTitle className="text-2xl font-bold text-center">{membership.name}</CardTitle>
                      <p className="text-center text-sm opacity-90 mt-1 font-medium">{membership.subtitle}</p>
                      <div className="text-center mt-3">
                        <div className="inline-block bg-white/20 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold">
                          {membership.servicesCovered} Service{membership.servicesCovered > 1 ? 's' : ''} Covered
                        </div>
                      </div>
                      <div className="text-center mt-4">
                        <div className="text-4xl font-bold">${membership.yearlyPrice}<span className="text-lg font-normal opacity-90">/year</span></div>
                        <div className="text-sm opacity-80 mt-1">Billed annually</div>
                      </div>
                    </CardHeader>

                    <CardContent className="p-6">
                      {/* Tagline */}
                      <p className={`text-sm ${membership.accentColor} font-medium mb-4 leading-relaxed`}>
                        {membership.tagline}
                      </p>

                      {/* Benefit-focused list */}
                      <ul className="space-y-3 mb-6">
                        {membership.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start gap-2.5 text-sm">
                            <Check className={`h-4 w-4 ${membership.accentColor} flex-shrink-0 mt-0.5`} />
                            <span className="text-slate-300">{benefit}</span>
                          </li>
                        ))}
                      </ul>

                      {/* Accordion Details */}
                      <Accordion type="single" collapsible className="mb-6">
                        <AccordionItem value="covered" className="border-b border-slate-700">
                          <AccordionTrigger className="text-sm font-semibold text-slate-300 hover:no-underline py-3">
                            What's Covered
                          </AccordionTrigger>
                          <AccordionContent>
                            <ul className="space-y-2 mt-1">
                              {membership.details.covered.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-xs">
                                  <Check className="h-3.5 w-3.5 text-green-400 flex-shrink-0 mt-0.5" />
                                  <span className="text-slate-400">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="not-covered" className="border-b border-slate-700">
                          <AccordionTrigger className="text-sm font-semibold text-slate-300 hover:no-underline py-3">
                            What's NOT Covered
                          </AccordionTrigger>
                          <AccordionContent>
                            <ul className="space-y-2 mt-1">
                              {membership.details.notCovered.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-xs">
                                  <X className="h-3.5 w-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                                  <span className="text-slate-400">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="terms" className="border-none">
                          <AccordionTrigger className="text-sm font-semibold text-slate-300 hover:no-underline py-3">
                            Terms & Conditions
                          </AccordionTrigger>
                          <AccordionContent>
                            <ul className="space-y-2 mt-1">
                              {membership.details.terms.map((item, idx) => (
                                <li key={idx} className="text-xs text-slate-500 pl-4">
                                  • {item}
                                </li>
                              ))}
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>

                      <Button
                        onClick={() => handleMembershipSelect(membership.name)}
                        className="w-full btn-gradient-orange text-white h-11 font-semibold transition-all duration-200 rounded-lg shadow-lg"
                        data-testid={`request-callback-${membership.name.toLowerCase().replace(' ', '-')}`}
                      >
                        <Phone className="h-4 w-4 mr-2" />
                        Request Callback
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Proration Note */}
            <p className="text-center text-sm text-slate-500 italic">
              *Membership options are priced per residential unit. Multi-unit properties are prorated based on the number of units covered.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 7 — WHAT HAPPENS WHEN IT BREAKS
          ═══════════════════════════════════════ */}
      <section className="py-16 sm:py-20 presidential-gradient" data-testid="membership-when-it-breaks">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" variants={stagger} viewport={{ once: true }}>
            <motion.div variants={fadeUp} className="text-center mb-10">
              <Wrench className="h-10 w-10 text-orange-400 mx-auto mb-4" />
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                What Happens If Something Breaks?
              </h2>
              <div className="h-1 w-16 bg-orange-500 mx-auto rounded-full" />
            </motion.div>

            <motion.div variants={fadeUp} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {[
                { icon: Ban, text: 'No claims or approvals' },
                { icon: Clock, text: 'No waiting weeks for decisions' },
                { icon: DollarSign, text: 'No surprise service fees' },
                { icon: Zap, text: 'We help you fix the issue quickly' },
                { icon: HeartHandshake, text: 'You pay reduced member rates — not inflated retail pricing' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  variants={fadeUp}
                  className={`flex items-start gap-3 p-4 rounded-lg border border-slate-700 bg-slate-800/30 ${idx === 4 ? 'sm:col-span-2 lg:col-span-1' : ''}`}
                >
                  <item.icon className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-300">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 8 — NOT A WARRANTY / IS A MEMBERSHIP
          ═══════════════════════════════════════ */}
      <section className="py-16 sm:py-20 bg-slate-900/50" data-testid="membership-clarification">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" variants={stagger} viewport={{ once: true }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* NOT a Warranty */}
              <motion.div variants={fadeUp}>
                <Card className="h-full border border-red-500/20 bg-red-500/5">
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="h-10 w-10 rounded-full bg-red-500/20 flex items-center justify-center">
                        <X className="h-5 w-5 text-red-400" />
                      </div>
                      <h3 className="text-xl font-bold text-red-400">This is NOT a Home Warranty</h3>
                    </div>
                    <ul className="space-y-3">
                      {['No claims process', 'No coverage loopholes', 'No denied repairs'].map((item, idx) => (
                        <li key={idx} className="flex items-center gap-3">
                          <X className="h-4 w-4 text-red-400 flex-shrink-0" />
                          <span className="text-sm text-slate-300">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>

              {/* IS a Membership */}
              <motion.div variants={fadeUp}>
                <Card className="h-full border border-green-500/20 bg-green-500/5">
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <CircleCheck className="h-5 w-5 text-green-400" />
                      </div>
                      <h3 className="text-xl font-bold text-green-400">This IS a Maintenance Membership</h3>
                    </div>
                    <ul className="space-y-3">
                      {[
                        'Ongoing care for your home',
                        'Prevention-first approach',
                        'Support when you actually need it'
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-center gap-3">
                          <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                          <span className="text-sm text-slate-300">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 9 — REFRAME ("UBER MOMENT")
          ═══════════════════════════════════════ */}
      <section className="py-16 sm:py-20 presidential-gradient" data-testid="membership-reframe">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" variants={stagger} viewport={{ once: true }}>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white mb-6">
              A Smarter Way to Manage Your Home
            </motion.h2>

            <motion.div variants={fadeUp} className="space-y-4 text-lg text-slate-400 leading-relaxed">
              <p>
                Most people handle their home the same way — wait until something breaks, then deal with it.
              </p>
              <p>
                That approach leads to stress, surprise costs, and bigger problems over time.
              </p>
              <p className="text-white font-medium">
                RemodWorks flips that model.
              </p>
              <p>
                We help you stay ahead of issues so your home runs smoothly — without the constant worry of what might break next.
              </p>
            </motion.div>

            <SectionDivider />

            <motion.p variants={fadeUp} className="text-xl sm:text-2xl font-semibold text-orange-400 mt-4">
              We don't wait for things to break — we help make sure they don't.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SECTION 10 — FINAL CTA + CALLBACK FORM
          ═══════════════════════════════════════ */}
      <section className="py-16 sm:py-20 bg-slate-900/50" data-testid="membership-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" variants={stagger} viewport={{ once: true }}>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Stop Dealing With Surprise Repairs?
            </motion.h2>

            <motion.p variants={fadeUp} className="text-lg text-slate-400 mb-8">
              We'll walk you through how it works and help you choose the right level of protection for your home.
            </motion.p>

            {!showForm && (
              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  onClick={() => { setShowForm(true); setTimeout(() => document.querySelector('#callback-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100); }}
                  className="btn-gradient-orange text-white h-12 px-8 font-semibold text-base rounded-lg shadow-lg"
                  data-testid="final-cta-callback-btn"
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Request Callback
                </Button>
                <a href="tel:+12342051010" className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Or call us: (234) 205-1010
                </a>
              </motion.div>
            )}
          </motion.div>

          {/* Callback Form */}
          {showForm && (
            <motion.div
              id="callback-form"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              transition={{ duration: 0.3 }}
              className="max-w-2xl mx-auto mt-8 text-left"
            >
              <Card className="border border-slate-600 shadow-xl bg-slate-800/50 backdrop-blur">
                <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-800 text-white p-6 rounded-t-lg">
                  <CardTitle className="text-2xl font-bold flex items-center gap-2">
                    <Phone className="h-6 w-6" />
                    Request a Callback
                  </CardTitle>
                  <p className="text-sm text-slate-300 mt-2">
                    {formData.membership && (<><span className="text-orange-400 font-medium">{formData.membership}</span> selected. </>)}
                    We'll call you within 24 hours to discuss the details.
                  </p>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="callback-name" className="text-sm font-medium text-slate-300">Full Name *</Label>
                      <Input id="callback-name" name="name" type="text" required value={formData.name} onChange={handleInputChange} placeholder="John Doe" className="mt-1.5" data-testid="callback-name-input" />
                    </div>
                    <div>
                      <Label htmlFor="callback-phone" className="text-sm font-medium text-slate-300">Phone Number *</Label>
                      <Input id="callback-phone" name="phone" type="tel" required value={formData.phone} onChange={handleInputChange} placeholder="(234) 205-1010" className="mt-1.5" data-testid="callback-phone-input" />
                    </div>
                    <div>
                      <Label htmlFor="callback-email" className="text-sm font-medium text-slate-300">Email (Optional)</Label>
                      <Input id="callback-email" name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="john@example.com" className="mt-1.5" data-testid="callback-email-input" />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Button type="submit" disabled={isSubmitting} className="flex-1 btn-gradient-orange text-white h-11 font-semibold disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-lg" data-testid="callback-submit-button">
                        {isSubmitting ? 'Submitting...' : 'Request Callback'}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="px-6 h-11">Cancel</Button>
                    </div>
                  </form>

                  {/* Customer Forms */}
                  <div className="mt-6 pt-6 border-t border-slate-700">
                    <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-orange-400" />
                      Customer Forms & Documents
                    </h4>
                    <p className="text-xs text-slate-400 mb-3">Review and download the service agreement before signing up.</p>
                    <a
                      href="/Remodworks_Maintenance_Service_Agreement.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg border border-slate-600 hover:border-orange-500/50 transition-all group"
                      data-testid="customer-agreement-download"
                    >
                      <Download className="h-4 w-4 text-orange-400 group-hover:text-orange-300" />
                      <span className="text-sm text-slate-300 group-hover:text-white">Maintenance Service Agreement</span>
                      <span className="text-xs text-slate-500 ml-auto">PDF</span>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};
